import React, { useEffect, useRef, useState } from 'react'
import { CButton, CCardBody, CCol, CFormLabel } from '@coreui/react-pro'
import { path, role } from '../../../libs'
import { Badge, Button, DatePicker, Input, InputRef, Space, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import CIcon from '@coreui/icons-react'
import { cilZoom } from '@coreui/icons'
import { DataIndex, DateFilter, Exchanger, Owner } from './types'
import { FilterValue } from 'antd/es/table/interface'
import { useExchangerList } from './hooks'
import { changedDateFormat } from './model'
import dayjs from 'dayjs'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import { useStore } from 'effector-react'
import { $userRole } from '../../../features/user'
import { Guard } from '../../../features/session/guard'

const dateFormat = 'YYYY-MM-DD'

export const ExchangersList = () => {
  const userRole = useStore($userRole)
  const { exchangers, onChangeExchangerList, loading, totalCountOfExchangers } =
    useExchangerList()

  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('deposits')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [dateFilter, setDateFilter] = useState<DateFilter | {}>({
    fromDate: dayjs().startOf('months').format(dateFormat),
    toDate: dayjs().format(dateFormat),
  })

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
    clearFilters()
    setSearchText((prev) => {
      delete prev[dataIndex]
      return {
        ...prev,
      }
    })
  }

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            setSearchText((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }))
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              setSearchText((prev) => ({
                ...prev,
                [dataIndex]: selectedKeys[0],
              }))
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchText[dataIndex] ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[dataIndex]]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  useEffect(() => {
    onChangeExchangerList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      dateFilter,
      filters: {
        type: filtersState?.type && (filtersState.type as string[]),
        active: filtersState.active && (filtersState.active[0] as boolean),
      },
    })
  }, [
    pageSize,
    page,
    orderBy,
    orderDirection,
    searchText,
    filtersState,
    dateFilter,
  ])

  const columns: ColumnsType<Exchanger> = [
    {
      title: 'ID',
      dataIndex: 'id',
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Exchanger',
      dataIndex: 'internalName',
      ...getColumnSearchProps('internalName'),
      key: 'internalName',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      filterSearch: true,
      filters: [
        {
          text: 'Partner',
          value: 'partner',
        },
        {
          text: 'Exchanger',
          value: 'exchanger',
        },
      ],
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      ...getColumnSearchProps('owner.fullName'),
      render: (item: Owner) => (
        <div>
          {item?.isTest ? (
            <Badge count={'Test'} offset={[-5, -10]}>
              <a
                href={path.clients.details(String(item.clientId))}
                target="_blank"
              >
                {item.fullName}
              </a>
            </Badge>
          ) : (
            <a
              href={path.clients.details(String(item.clientId))}
              target="_blank"
            >
              {item.fullName}
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'frontName',
      key: 'frontName',
      ...getColumnSearchProps('frontName'),
    },
    {
      title: 'Gateway',
      dataIndex: 'gatewayId',
      key: 'gatewayId',
      ...getColumnSearchProps('gatewayId'),
      render: (item: string) => {
        const restrictedRoles = new Set([
          role.partnership.juniorPartnerManager(),
        ])

        if (
          Array.isArray(userRole) &&
          userRole.some((role) => restrictedRoles.has(role))
        ) {
          return <span>{item}</span>
        } else {
          return (
            <a href={path.billing.gateway.edit(String(item))} target="_blank">
              {item}
            </a>
          )
        }
      },
    },
    {
      title: 'Deposits',
      dataIndex: 'deposits',
      key: 'deposits',
      sorter: true,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Withdrawal',
      dataIndex: 'withdrawal',
      key: 'withdrawal',
      sorter: true,
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      sorter: true,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      filterMultiple: false,
      render: (item) => {
        return <div>{item ? 'yes' : 'no'}</div>
      },
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 100,
      render: (item) => {
        return (
          <Guard permission="edit_exchanger_wallet">
            <CButton
              color="secondary"
              className="me-2"
              title="View"
              href={path.exchangers.edit(item.id)}
            >
              <CIcon icon={cilZoom} />
            </CButton>
          </Guard>
        )
      },
    },
  ]

  return (
    <CCardBody>
      <CCol className="d-flex align-items-baseline justify-content-between gap-2">
        <div className="d-flex gap-1">
          <Guard permission="create_exchanger_wallet">
            <CButton
              color="primary"
              className="mb-2"
              href={path.exchangers.create()}
            >
              Create exchanger wallet
            </CButton>
          </Guard>
        </div>
        <div className="d-flex  gap-2">
          <CFormLabel className="col-form-label">Date</CFormLabel>
          <DatePicker.RangePicker
            format="YYYY-MM-DD"
            defaultValue={[
              // @ts-ignore
              dayjs(dateFilter.fromDate, dateFormat),
              // @ts-ignore
              dayjs(dateFilter.toDate, dateFormat),
            ]}
            style={{ display: 'flex' }}
            onChange={(e) => {
              if (!e) {
                setDateFilter({})
              }
              if (e) {
                setDateFilter((prev) => ({
                  ...prev,
                  // @ts-ignore
                  fromDate: e[0] ? changedDateFormat(e[0]?.$d) : e[0],
                }))
              }
              if (e) {
                setDateFilter((prev) => ({
                  ...prev,
                  // @ts-ignore
                  toDate: e[1] ? changedDateFormat(e[1]?.$d) : e[1],
                }))
              }
            }}
            open={isCalendarOpen}
            onClick={() => setIsCalendarOpen(true)}
            allowEmpty={[true, true]}
            onCalendarChange={() => setIsCalendarOpen(false)}
          />
        </div>
      </CCol>
      <Table
        dataSource={exchangers}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="id"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfExchangers,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current,
            //@ts-ignore
            currentPageSize: pagination.pageSize,
            //@ts-ignore
            currentOrderDirection: sorter.order,
            //@ts-ignore
            currentOrderBy: sorter.field,
            filters,
          })
        }
      ></Table>
    </CCardBody>
  )
}
